export type TrackId = 'get-started' | 'training' | 'architecture' | 'services' | 'operations';
export type Level = 'Beginner' | 'Intermediate' | 'Advanced';

export interface DocSection {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  steps?: string[];
  note?: { tone: 'info' | 'warning' | 'success'; title: string; body: string };
  code?: { language: string; value: string };
}

export interface DocArticle {
  slug: string;
  title: string;
  summary: string;
  track: TrackId;
  category: string;
  service?: string;
  level: Level;
  minutes: number;
  reviewed: string;
  sourcePaths: string[];
  sections: DocSection[];
}

export interface Track {
  id: TrackId;
  title: string;
  shortTitle: string;
  description: string;
  outcome: string;
}

export const tracks: Track[] = [
  {
    id: 'get-started',
    title: 'Get Started',
    shortTitle: 'Start',
    description: 'Learn cloud fundamentals, create a safe Nexus workspace, and deploy your first complete service.',
    outcome: 'Go from no cloud experience to a working server, network, storage volume, and public endpoint.',
  },
  {
    id: 'training',
    title: 'Nexus Training',
    shortTitle: 'Train',
    description: 'Guided labs that build practical operational skill across the Nexus platform.',
    outcome: 'Operate common workloads confidently and understand how services connect.',
  },
  {
    id: 'architecture',
    title: 'Cloud Architecture',
    shortTitle: 'Design',
    description: 'Reference designs, decision guides, and patterns for company and project environments.',
    outcome: 'Design secure, maintainable, and cost-aware cloud systems for real workloads.',
  },
  {
    id: 'services',
    title: 'Service Guides',
    shortTitle: 'Build',
    description: 'Detailed overviews and procedures for every current Nexus service.',
    outcome: 'Find exact concepts, workflows, dependencies, and operational guidance by service.',
  },
  {
    id: 'operations',
    title: 'Operations',
    shortTitle: 'Operate',
    description: 'Billing, usage, lifecycle, troubleshooting, quotas, support, and platform governance.',
    outcome: 'Run Nexus resources safely throughout their full lifecycle.',
  },
];

const reviewed = 'June 24, 2026';

const section = (
  id: string,
  title: string,
  paragraphs: string[],
  options: Partial<Omit<DocSection, 'id' | 'title' | 'paragraphs'>> = {},
): DocSection => ({ id, title, paragraphs, ...options });

const serviceArticle = (input: {
  slug: string;
  title: string;
  summary: string;
  category: string;
  service: string;
  sourcePaths: string[];
  concepts: string[];
  workflow: string[];
  practices: string[];
  dependencies?: string[];
  level?: Level;
  minutes?: number;
}): DocArticle => ({
  slug: input.slug,
  title: input.title,
  summary: input.summary,
  track: 'services',
  category: input.category,
  service: input.service,
  level: input.level ?? 'Beginner',
  minutes: input.minutes ?? 12,
  reviewed,
  sourcePaths: input.sourcePaths,
  sections: [
    section('overview', 'Overview', [
      input.summary,
      `This guide explains where ${input.title} fits in Nexus, how its resources behave, and which decisions you should make before creating production resources.`,
    ]),
    {
      id: 'concepts',
      title: 'Core concepts',
      bullets: input.concepts,
    },
    ...(input.dependencies?.length
      ? [{
          id: 'dependencies',
          title: 'Before you begin',
          paragraphs: ['Confirm these dependencies before provisioning:'],
          bullets: input.dependencies,
        } satisfies DocSection]
      : []),
    {
      id: 'workflow',
      title: 'Standard workflow',
      steps: input.workflow,
    },
    {
      id: 'operations',
      title: 'Production practices',
      bullets: input.practices,
      note: {
        tone: 'info',
        title: 'Usage and billing',
        body: 'Review the service Usage view after provisioning. Nexus tracks billable resources from creation until deletion or release, even when a resource is idle unless the service explicitly states otherwise.',
      },
    },
  ],
});

export const articles: DocArticle[] = [
  {
    slug: 'welcome-to-nexus',
    title: 'Welcome to Elysia Nexus',
    summary: 'Understand the Nexus platform, its resource model, and the path from account setup to a running cloud architecture.',
    track: 'get-started',
    category: 'Orientation',
    level: 'Beginner',
    minutes: 8,
    reviewed,
    sourcePaths: ['frontend/src/components/nexus/NexusSidebar.tsx', 'frontend/src/pages/nexus/NexusDashboard.tsx'],
    sections: [
      section('what-is-nexus', 'What Nexus is', [
        'Elysia Nexus is a public cloud ecosystem for creating compute, networking, storage, data, security, and application resources from one control plane.',
        'You work inside a tenant. A tenant is the ownership and billing boundary for a company, team, or project. Resources, members, invoices, payment methods, support plans, and quotas are evaluated in that tenant context.',
      ]),
      {
        id: 'mental-model',
        title: 'The five-layer mental model',
        bullets: [
          'Identity: your account, tenant membership, and role determine what you can see and change.',
          'Location: a region or location determines where physical resources are placed.',
          'Network: virtual networks, routers, security groups, and public IPs control connectivity.',
          'Workload: servers, app deployments, databases, and load balancers run your application.',
          'Data and trust: volumes, object storage, secrets, images, and PKI preserve state and identity.',
        ],
      },
      {
        id: 'first-path',
        title: 'Recommended first path',
        steps: [
          'Complete your billing profile and select or create the correct tenant.',
          'Read Cloud fundamentals, then plan a small private network.',
          'Create a server with a key pair or strong initial password.',
          'Allocate and attach a public IP only if the server must be directly reachable.',
          'Add storage and a backup strategy before placing important data on the server.',
          'Use the Usage dashboard to verify the resources that are accruing charges.',
        ],
        note: {
          tone: 'success',
          title: 'Start small',
          body: 'A single small server on one private network is enough for the first lab. Add managed services only after you understand the traffic and data path.',
        },
      },
    ],
  },
  {
    slug: 'cloud-fundamentals',
    title: 'Cloud Fundamentals for New Users',
    summary: 'Learn the vocabulary behind regions, virtual machines, networks, addresses, storage, availability, and shared responsibility.',
    track: 'get-started',
    category: 'Foundations',
    level: 'Beginner',
    minutes: 15,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/services'],
    sections: [
      section('resources', 'Cloud resources are software-defined', [
        'A cloud resource is an object you create through the control panel. It represents capacity or configuration on provider infrastructure. Servers consume CPU and memory, volumes consume durable storage, and public IPs consume globally routable address space.',
        'Resources have lifecycles. Creation may be asynchronous, state changes can take time, and deletion is distinct from stopping. Stopped compute can retain attached storage and allocated addresses.',
      ]),
      {
        id: 'terms',
        title: 'Essential terms',
        bullets: [
          'Region or location: the geographic and infrastructure placement boundary.',
          'Availability zone: a provider placement domain within a location.',
          'Image: the operating system template used to create a server.',
          'Flavor: the CPU and memory shape assigned to a server.',
          'Private IP: an address used inside virtual networks.',
          'Public or floating IP: an internet-routable address allocated to a tenant.',
          'Ingress and egress: traffic entering and leaving a resource or network.',
          'Persistent storage: data designed to survive server replacement.',
        ],
      },
      section('responsibility', 'Shared responsibility', [
        'Nexus operates the cloud control plane and underlying service integrations. You remain responsible for guest operating systems, credentials, application updates, firewall intent, data classification, backups, and the architecture you deploy.',
      ], {
        note: {
          tone: 'warning',
          title: 'Cloud does not mean automatically backed up',
          body: 'Create an explicit backup and restore plan. A running server, attached volume, or replicated platform service is not by itself a complete backup strategy.',
        },
      }),
    ],
  },
  {
    slug: 'accounts-tenants-members',
    title: 'Accounts, Tenants, and Members',
    summary: 'Set up the ownership boundary for your organization and give people the access they need.',
    track: 'get-started',
    category: 'Identity',
    level: 'Beginner',
    minutes: 10,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/NexusTenants.tsx', 'frontend/src/pages/nexus/NexusMembers.tsx'],
    sections: [
      section('tenant', 'Choose the right tenant boundary', [
        'Use a tenant for a stable ownership and billing boundary. Separate tenants are appropriate when projects need independent members, payment methods, invoices, quotas, or operational isolation.',
        'Do not create a tenant for every temporary resource. Use names and service organization inside one tenant when the same team and billing owner manage the environment.',
      ]),
      {
        id: 'membership',
        title: 'Member workflow',
        steps: [
          'Open Tenants and select the tenant you intend to manage.',
          'Open Members and invite or add the required user.',
          'Assign the least-privileged role that supports the user’s work.',
          'Confirm the user can switch to the tenant and see only expected resources.',
          'Review membership when people change teams or leave the organization.',
        ],
      },
      {
        id: 'governance',
        title: 'Governance checklist',
        bullets: [
          'Use individual accounts instead of shared credentials.',
          'Keep at least two authorized tenant administrators for business continuity.',
          'Document the owner and purpose of each tenant.',
          'Use support tickets for access recovery instead of creating duplicate accounts.',
        ],
      },
    ],
  },
  {
    slug: 'first-nexus-environment',
    title: 'Build Your First Nexus Environment',
    summary: 'A complete beginner walkthrough for a private network, server, storage volume, and controlled public access.',
    track: 'get-started',
    category: 'Quickstart',
    level: 'Beginner',
    minutes: 30,
    reviewed,
    sourcePaths: [
      'frontend/src/pages/nexus/services/virtual-networks/NetworksPage.tsx',
      'frontend/src/pages/nexus/services/cloud-compute/ComputeCreatePage.tsx',
      'frontend/src/pages/nexus/services/volumes/VolumesCreatePage.tsx',
      'frontend/src/pages/nexus/services/edge-resources/PublicIPsPage.tsx',
    ],
    sections: [
      section('plan', 'Plan the environment', [
        'This exercise creates one isolated application network, one Linux server, one persistent volume, and one public IP. It is intentionally small so every dependency remains visible.',
      ], {
        bullets: [
          'Use a non-overlapping private CIDR such as 10.20.0.0/24.',
          'Choose the location before creating dependent resources.',
          'Prepare an SSH key pair or a strong password stored in a password manager.',
          'Decide which single management source address should be allowed through the security group.',
        ],
      }),
      {
        id: 'build',
        title: 'Build sequence',
        steps: [
          'Create a virtual network and subnet in your selected location.',
          'Create or attach a router so the subnet has the required external path.',
          'Create a restrictive security group that allows SSH only from your trusted source.',
          'Create a server using the network, security group, image, and flavor you selected.',
          'Create a volume in the same compatible location and attach it to the server.',
          'Allocate a public IP and associate it with the server port only when external access is required.',
          'Connect, patch the operating system, and verify the attached data disk.',
          'Open Active Services and Usage to confirm ownership and expected billing.',
        ],
      },
      section('finish', 'Completion criteria', [
        'You are finished when the server is active, private networking works, the volume is visible inside the guest, and only the intended public ports are reachable.',
      ], {
        note: {
          tone: 'warning',
          title: 'Release unused addresses',
          body: 'An allocated public IP can remain billable while unattached. Release it when the environment no longer needs it.',
        },
      }),
    ],
  },
  {
    slug: 'understand-nexus-billing',
    title: 'Understand Nexus Billing',
    summary: 'Learn how metered resources, usage records, credits, invoices, payment methods, and service eligibility fit together.',
    track: 'get-started',
    category: 'Billing',
    service: 'billing',
    level: 'Beginner',
    minutes: 12,
    reviewed,
    sourcePaths: [
      'frontend/src/pages/nexus/NexusBillingOverview.tsx',
      'frontend/src/pages/nexus/NexusUsageDashboard.tsx',
      'frontend/src/pages/nexus/NexusInvoices.tsx',
    ],
    sections: [
      section('metering', 'How usage accrues', [
        'Nexus services can be free, fixed-price, or usage-metered. Metered resources generally accrue usage while the billable resource exists, not only while an application is actively using it.',
        'A stopped server can still retain billable storage and public IP allocations. Deleting compute does not necessarily release independently managed resources.',
      ]),
      {
        id: 'views',
        title: 'Billing views',
        bullets: [
          'Overview summarizes account standing and the current billing position.',
          'Usage breaks consumption down by service and resource.',
          'Invoices show finalized billing documents and their status.',
          'Payment Methods controls eligible methods for automated or manual settlement.',
          'Credits records available account credit and credit activity.',
        ],
      },
      {
        id: 'control',
        title: 'Control cost',
        steps: [
          'Tag or name resources so the owner and purpose are clear.',
          'Review Active Services and Usage every week.',
          'Release unused public IPs and delete abandoned volumes, images, and snapshots.',
          'Right-size compute after measuring CPU, RAM, disk, and network behavior.',
          'Use quota limits to prevent accidental growth.',
        ],
      },
    ],
  },
  {
    slug: 'support-and-service-health',
    title: 'Support and Service Health',
    summary: 'Know when to troubleshoot, when to open a ticket, and what evidence helps support resolve an issue quickly.',
    track: 'get-started',
    category: 'Support',
    service: 'support',
    level: 'Beginner',
    minutes: 8,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/NexusSupportPage.tsx', 'frontend/src/pages/nexus/NexusSupportPlanPage.tsx'],
    sections: [
      section('before-ticket', 'Before opening a ticket', [
        'Check the resource state, location, recent actions, account eligibility, and service usage. Retry only when an action is clearly safe to repeat.',
      ], {
        bullets: [
          'Record the tenant, resource name, resource ID, service, and location.',
          'Capture the exact error text and the time it occurred.',
          'Describe the expected result and the actual result.',
          'Do not include passwords, private keys, access keys, or secret values.',
        ],
      }),
      {
        id: 'ticket',
        title: 'Create an effective ticket',
        steps: [
          'Select the department and issue category that best matches the affected service.',
          'Use a specific title that names the resource and failure.',
          'Provide reproduction steps and business impact.',
          'Attach sanitized logs or screenshots when they clarify the issue.',
          'Keep follow-up information in the same ticket.',
        ],
      },
    ],
  },
  {
    slug: 'training-foundations',
    title: 'Training 01: Nexus Foundations',
    summary: 'A guided lab covering tenant context, service discovery, locations, resource identity, and usage visibility.',
    track: 'training',
    category: 'Core curriculum',
    level: 'Beginner',
    minutes: 35,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/NexusDashboard.tsx', 'frontend/src/pages/nexus/NexusCatalog.tsx'],
    sections: [
      section('objectives', 'Objectives', ['By the end of this lab, you will be able to navigate Nexus and explain the ownership, placement, lifecycle, and billing context of a resource.']),
      {
        id: 'lab',
        title: 'Lab',
        steps: [
          'Switch between available tenants and observe which resource views change.',
          'Open Service Catalog and identify enabled services and their billing mode.',
          'Open Active Services and group existing resources by compute, network, data, and security.',
          'Choose one resource and locate its ID, location, status, and creation time.',
          'Find the same service in Usage and identify its billing unit.',
          'Write a one-sentence ownership and lifecycle description for the resource.',
        ],
      },
      {
        id: 'check',
        title: 'Knowledge check',
        bullets: [
          'Why is a tenant more than a folder?',
          'Which attributes must be compatible before attaching two resources?',
          'Why can an idle resource continue to accrue charges?',
        ],
      },
    ],
  },
  {
    slug: 'training-compute',
    title: 'Training 02: Compute Operations',
    summary: 'Create, access, observe, resize, stop, restart, and safely retire a Nexus server.',
    track: 'training',
    category: 'Core curriculum',
    level: 'Beginner',
    minutes: 50,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/services/cloud-compute'],
    sections: [
      section('objectives', 'Objectives', ['Practice the complete compute lifecycle and learn the difference between power state, provisioning state, and billable resource existence.']),
      {
        id: 'lab',
        title: 'Lab',
        steps: [
          'Create a key pair and store the private key securely.',
          'Create a small server on a private network with a restrictive security group.',
          'Open the server detail page and identify flavor, image, addresses, volumes, and task state.',
          'Use the console for recovery-oriented access, then use SSH for normal administration.',
          'Observe live CPU, memory, disk, and network metrics during a simple workload.',
          'Stop and restart the instance, noting which attached resources remain.',
          'Change the root password only if the image and guest agent support the operation.',
          'Delete the training server and separately verify whether volumes and public IPs remain.',
        ],
      },
    ],
  },
  {
    slug: 'training-networking',
    title: 'Training 03: Networking and Security',
    summary: 'Build a routed private network, apply security groups, and publish one controlled endpoint.',
    track: 'training',
    category: 'Core curriculum',
    level: 'Intermediate',
    minutes: 60,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/services/virtual-networks', 'frontend/src/pages/nexus/services/edge-resources'],
    sections: [
      section('objectives', 'Objectives', ['Understand packet flow from an internet client through a public IP and security policy to a private server port.']),
      {
        id: 'lab',
        title: 'Lab',
        steps: [
          'Create a network and subnet using an RFC1918 address range.',
          'Create a router and connect the subnet.',
          'Create separate management and application security groups.',
          'Deploy a server with only the management group attached.',
          'Allocate a public IP and associate it with the server.',
          'Verify blocked application traffic, attach the application group, then verify allowed traffic.',
          'Remove the application rule and confirm access is revoked.',
          'Draw the final traffic path and identify every policy decision point.',
        ],
      },
      section('review', 'Review', ['A public IP creates reachability, but a security group determines permitted traffic. Application-level authentication and host firewalls remain separate controls.']),
    ],
  },
  {
    slug: 'training-storage-data',
    title: 'Training 04: Storage and Data',
    summary: 'Compare block, object, database, and secret storage by operating one workload across all four.',
    track: 'training',
    category: 'Core curriculum',
    level: 'Intermediate',
    minutes: 70,
    reviewed,
    sourcePaths: [
      'frontend/src/pages/nexus/services/volumes',
      'frontend/src/pages/nexus/services/s3',
      'frontend/src/pages/nexus/services/database',
      'frontend/src/pages/nexus/services/secrets',
    ],
    sections: [
      section('choose', 'Choose storage by access pattern', [
        'Block storage presents a disk to a server. Object storage presents buckets and objects over an API. Database service manages a database lifecycle. Secrets stores sensitive values and metadata.',
      ]),
      {
        id: 'lab',
        title: 'Lab',
        steps: [
          'Create and attach a volume, then format and mount it inside a training server.',
          'Create an object storage credential and bucket, then upload a non-sensitive test object.',
          'Create a database and database user, then connect from the private application network.',
          'Store the database credential in Secrets instead of source code.',
          'Document the backup, restore, rotation, and deletion procedure for each data type.',
        ],
      },
    ],
  },
  {
    slug: 'training-production-readiness',
    title: 'Training 05: Production Readiness',
    summary: 'Turn a working environment into an operable service with health checks, TLS, backups, quotas, and cost ownership.',
    track: 'training',
    category: 'Advanced curriculum',
    level: 'Advanced',
    minutes: 75,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus'],
    sections: [
      section('readiness', 'Production readiness is a system property', [
        'A workload is not production-ready merely because it responds to requests. It must be observable, recoverable, secured, supportable, and financially owned.',
      ]),
      {
        id: 'exercise',
        title: 'Readiness review',
        bullets: [
          'Availability: remove single points of failure where the business requirement justifies it.',
          'Health: define load balancer checks and external service checks.',
          'Recovery: test restoring data into a replacement environment.',
          'Security: use least privilege, secret rotation, TLS, and narrow ingress.',
          'Operations: define alerts, owners, maintenance windows, and escalation paths.',
          'Cost: assign a budget, inspect usage, and define deletion criteria.',
        ],
      },
      {
        id: 'deliverable',
        title: 'Deliverable',
        steps: [
          'Create a one-page architecture diagram.',
          'Create a resource inventory with owner and recovery priority.',
          'Write a failure runbook for compute loss, credential exposure, and data loss.',
          'Record monthly expected cost and the metrics that trigger resizing.',
        ],
      },
    ],
  },
  {
    slug: 'architecture-principles',
    title: 'Nexus Architecture Principles',
    summary: 'Use stable design principles to make cloud systems secure, recoverable, understandable, and cost-aware.',
    track: 'architecture',
    category: 'Principles',
    level: 'Intermediate',
    minutes: 18,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/services'],
    sections: [
      {
        id: 'principles',
        title: 'Seven principles',
        bullets: [
          'Design from business requirements, not from the service catalog.',
          'Keep application tiers private unless they must receive internet traffic directly.',
          'Treat instances as replaceable and persistent data as explicitly managed.',
          'Separate secrets from code, images, and deployment configuration.',
          'Prefer narrow, observable paths over broad network access.',
          'Build recovery procedures before the first incident.',
          'Measure usage and cost as part of system health.',
        ],
      },
      section('boundaries', 'Choose boundaries deliberately', [
        'Use tenants for ownership and billing separation, networks for traffic boundaries, security groups for workload policy, and service identities or credentials for application access.',
      ]),
      section('failure', 'Design for failure domains', [
        'Identify what happens if a server, volume, network path, credential, location, or human operator becomes unavailable. The appropriate response may be redundancy, replacement automation, backup restoration, or an explicit accepted risk.',
      ]),
    ],
  },
  {
    slug: 'architecture-public-web',
    title: 'Architecture: Public Web Application',
    summary: 'A reference pattern for a TLS-enabled web application with private compute and a public load balancer.',
    track: 'architecture',
    category: 'Reference designs',
    level: 'Intermediate',
    minutes: 20,
    reviewed,
    sourcePaths: [
      'frontend/src/pages/nexus/services/load-balancers',
      'frontend/src/pages/nexus/services/dns',
      'frontend/src/pages/nexus/services/pki',
    ],
    sections: [
      section('pattern', 'Pattern', [
        'Place application servers on a private subnet. Publish the service through a load balancer with health checks. Point DNS at the public endpoint and use PKI or ACME-managed certificates for TLS.',
      ]),
      {
        id: 'components',
        title: 'Components',
        bullets: [
          'Virtual network and private application subnet.',
          'Two or more compute instances or an app deployment.',
          'Application security group accepting traffic only from the load balancer path.',
          'Load balancer listener, pool, members, and health monitor.',
          'DNS zone and application record.',
          'TLS certificate and renewal ownership.',
          'Object storage for static assets or backups where appropriate.',
        ],
      },
      {
        id: 'sequence',
        title: 'Build sequence',
        steps: [
          'Create the network and private compute resources.',
          'Deploy and validate the application privately.',
          'Create the load balancer and health monitor.',
          'Attach healthy members and validate the public endpoint.',
          'Issue or import TLS material and configure the listener.',
          'Create DNS records only after the endpoint is stable.',
          'Add monitoring and test member failure.',
        ],
      },
    ],
  },
  {
    slug: 'architecture-three-tier',
    title: 'Architecture: Three-Tier Business Application',
    summary: 'Separate edge, application, and data responsibilities for a maintainable company workload.',
    track: 'architecture',
    category: 'Reference designs',
    level: 'Advanced',
    minutes: 25,
    reviewed,
    sourcePaths: [
      'frontend/src/pages/nexus/services/cloud-compute',
      'frontend/src/pages/nexus/services/database',
      'frontend/src/pages/nexus/services/secrets',
    ],
    sections: [
      section('tiers', 'Tier responsibilities', [
        'The edge tier terminates public traffic, the application tier runs business logic, and the data tier stores durable state. Each tier should expose only the protocol required by the next tier.',
      ], {
        bullets: [
          'Edge: load balancer, DNS, TLS, and request health.',
          'Application: private compute or app deployments with horizontal replacement capability.',
          'Data: database service, persistent storage, backup, and controlled credentials.',
        ],
      }),
      {
        id: 'policy',
        title: 'Policy model',
        bullets: [
          'Internet to edge: allow only required public listeners.',
          'Edge to application: allow only the application port.',
          'Application to database: allow only the database protocol from the application group.',
          'Operators to management: use a narrow management path and individual credentials.',
        ],
      },
      section('scale', 'Scaling decisions', [
        'Scale the tier that is constrained. Adding application instances will not fix a database bottleneck, and increasing compute size will not fix an unhealthy dependency. Use service metrics and request behavior to identify the limiting tier.',
      ]),
    ],
  },
  {
    slug: 'architecture-static-delivery',
    title: 'Architecture: Static Content and Object Delivery',
    summary: 'Store immutable content in S3-compatible object storage and separate delivery from application compute.',
    track: 'architecture',
    category: 'Reference designs',
    level: 'Intermediate',
    minutes: 16,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/services/s3'],
    sections: [
      section('pattern', 'Pattern', [
        'Use object storage for files addressed as objects rather than as a mounted filesystem. Applications upload and retrieve through S3-compatible credentials while metadata and authorization remain in the application domain.',
      ]),
      {
        id: 'decisions',
        title: 'Design decisions',
        bullets: [
          'Choose bucket boundaries by lifecycle and access policy.',
          'Use separate credentials for applications, operators, and automation.',
          'Never embed long-lived access keys in frontend code.',
          'Define retention, versioning, deletion, and restore behavior.',
          'Use immutable object names or versioned paths for cache-safe releases.',
        ],
      },
    ],
  },
  {
    slug: 'architecture-hybrid-connectivity',
    title: 'Architecture: Hybrid and Routed Connectivity',
    summary: 'Plan IP transit, BGP sessions, prefixes, and private address space for connected environments.',
    track: 'architecture',
    category: 'Connectivity',
    level: 'Advanced',
    minutes: 28,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/services/ip-transit'],
    sections: [
      section('scope', 'When to use routed connectivity', [
        'Use IP transit and BGP when a project must exchange routed prefixes with external networks under explicit routing policy. This is an advanced networking capability, not a replacement for a public IP on a single workload.',
      ]),
      {
        id: 'plan',
        title: 'Plan before provisioning',
        bullets: [
          'Confirm ASN ownership or assignment requirements.',
          'Validate prefix authority and route objects where applicable.',
          'Prevent overlap between cloud, office, datacenter, VPN, and partner networks.',
          'Define accepted and advertised prefixes, maximum-prefix limits, and failure behavior.',
          'Prepare contact and maintenance procedures for both sides of the session.',
        ],
      },
      {
        id: 'operate',
        title: 'Operational controls',
        bullets: [
          'Use explicit prefix filters.',
          'Monitor session state and route changes.',
          'Test failover without introducing a routing loop.',
          'Keep architecture and routing documentation synchronized.',
        ],
      },
    ],
  },
  {
    slug: 'architecture-cost-aware',
    title: 'Architecture: Cost-Aware Environments',
    summary: 'Translate technical resource choices into a predictable operating budget.',
    track: 'architecture',
    category: 'Governance',
    level: 'Intermediate',
    minutes: 18,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/NexusUsageDashboard.tsx', 'frontend/src/pages/nexus/NexusActiveServices.tsx'],
    sections: [
      section('model', 'Model cost by resource lifecycle', [
        'Estimate each continuously allocated resource using its hourly price and expected hours. Add storage by provisioned capacity, public addresses, managed services, and support plan costs. Include a margin for growth and data protection.',
      ], {
        code: {
          language: 'text',
          value: 'monthly estimate = sum(hourly rate x expected hours x quantity) + storage + fixed services',
        },
      }),
      {
        id: 'controls',
        title: 'Cost controls',
        bullets: [
          'Use quotas as guardrails, not as a substitute for monitoring.',
          'Create separate environments only when the isolation benefit justifies persistent cost.',
          'Schedule deletion of temporary test resources.',
          'Track allocated but unattached public IPs and volumes.',
          'Resize from observed demand instead of assumptions.',
        ],
      },
    ],
  },
  serviceArticle({
    slug: 'service-cloud-compute',
    title: 'Cloud Compute',
    summary: 'Create and operate virtual server instances with selectable images, flavors, networks, storage, credentials, and lifecycle actions.',
    category: 'Compute',
    service: 'cloud-compute',
    sourcePaths: ['frontend/src/pages/nexus/services/cloud-compute'],
    concepts: [
      'Flavor defines virtual CPU and memory capacity.',
      'Image defines the initial operating system or boot template.',
      'Tenant-owned SSH public keys, initial passwords, or both can provide guest access.',
      'Reinstall is a two-step replacement workflow: image, boot volume, and cloud-init first; authentication second.',
      'Networks and security groups define connectivity and permitted traffic.',
      'Power state is different from resource existence and billing state.',
    ],
    dependencies: ['An eligible tenant and selected location.', 'A virtual network or supported network option.', 'An image and flavor compatible with the workload.'],
    workflow: ['Add an OpenSSH public key when SSH access is required.', 'Choose Create Server and select location, image, flavor, network, and password, SSH key, or combined authentication.', 'For reinstall, first choose the image and replacement boot volume, then confirm the image cloud-init or provide an override.', 'On reinstall step two, select password, SSH key, or both and confirm the destructive operation.', 'Wait for ACTIVE state.', 'Validate console or SSH access.', 'Observe metrics and attach persistent storage as required.'],
    practices: ['Never upload or store an SSH private key in Nexus.', 'Patch the guest operating system promptly.', 'Keep important data off disposable root disks.', 'Use narrow security-group rules.', 'Measure before resizing.', 'Delete dependent resources separately when retiring a server.'],
  }),
  serviceArticle({
    slug: 'service-image-registry',
    title: 'Image Registry',
    summary: 'Upload, inspect, and manage reusable machine images for Nexus compute workloads.',
    category: 'Compute',
    service: 'image-registry',
    sourcePaths: ['frontend/src/pages/nexus/services/image-registry'],
    concepts: ['Images are reusable boot templates.', 'Upload jobs can have processing states.', 'Image format and minimum disk requirements affect compatibility.', 'Private images belong to the tenant context.'],
    workflow: ['Prepare a supported image file.', 'Create an upload and provide required metadata.', 'Monitor processing until the image is ready.', 'Inspect image details and compatibility.', 'Select the image during server creation.'],
    practices: ['Remove embedded credentials and machine identity.', 'Patch and scan base images.', 'Version images immutably.', 'Document image ownership and retirement dates.'],
  }),
  serviceArticle({
    slug: 'service-app-deployments',
    title: 'App Deployments',
    summary: 'Deploy application processes from source configuration with domains, secrets, runtime controls, and usage visibility.',
    category: 'Compute',
    service: 'app-deployments',
    sourcePaths: ['frontend/src/pages/nexus/services/app-deployments', 'docs/app-deployments-config-spec.md'],
    concepts: ['A deployment groups application processes and configuration.', 'elysia.json describes supported process topology.', 'Domains connect ingress to an application.', 'Secrets are injected separately from source.', 'Runtime protections constrain unsafe process behavior.'],
    workflow: ['Connect or select a source repository.', 'Define the deployment configuration.', 'Create the deployment and monitor build status.', 'Add secrets and a domain.', 'Validate health and inspect usage.'],
    practices: ['Pin dependencies.', 'Keep secrets out of repositories.', 'Define health behavior.', 'Use separate deployment environments.', 'Review process memory and count before scaling.'],
    level: 'Intermediate',
  }),
  serviceArticle({
    slug: 'service-virtual-networks',
    title: 'Virtual Networks',
    summary: 'Create tenant-isolated networks, subnets, and routers for private workload connectivity.',
    category: 'Networking',
    service: 'virtual-networks',
    sourcePaths: ['frontend/src/pages/nexus/services/virtual-networks'],
    concepts: ['A network is a Layer 2 connectivity domain.', 'A subnet supplies IP addressing and gateway configuration.', 'A router connects subnets and external paths.', 'CIDR choices must not overlap with connected environments.'],
    workflow: ['Choose a private CIDR plan.', 'Create a network and subnet.', 'Create or select a router.', 'Attach the subnet to the router.', 'Connect workloads and verify private traffic.'],
    practices: ['Use predictable address plans.', 'Keep tiers in intentional network boundaries.', 'Document gateways and routes.', 'Avoid public exposure when private connectivity is sufficient.'],
  }),
  serviceArticle({
    slug: 'service-edge-resources',
    title: 'Edge Resources and Public IPs',
    summary: 'Allocate public addresses, associate floating IPs, and control edge exposure with security groups.',
    category: 'Networking',
    service: 'edge-resources',
    sourcePaths: ['frontend/src/pages/nexus/services/edge-resources'],
    concepts: ['Allocated IPs belong to a tenant until released.', 'A floating IP maps public reachability to a private port.', 'Unattached allocations can still be billable.', 'Security groups filter traffic independently of address allocation.'],
    workflow: ['Select a location and available public network.', 'Allocate an IP.', 'Associate it with the intended workload port.', 'Apply restrictive security rules.', 'Validate reachability and release the IP when no longer required.'],
    practices: ['Do not allocate addresses speculatively.', 'Use load balancers for shared application ingress.', 'Restrict management ports by source.', 'Track every allocation in Active Services.'],
  }),
  serviceArticle({
    slug: 'service-load-balancers',
    title: 'Load Balancers',
    summary: 'Distribute application traffic across healthy backend members using listeners, pools, and health monitors.',
    category: 'Networking',
    service: 'load-balancers',
    sourcePaths: ['frontend/src/pages/nexus/services/load-balancers'],
    concepts: ['Listener accepts client traffic.', 'Pool groups backend targets.', 'Member identifies a backend address and port.', 'Health monitor removes unhealthy members from service.', 'Capabilities depend on the selected provider and location.'],
    workflow: ['Create a load balancer on the required network.', 'Add a listener and pool.', 'Register backend members.', 'Configure a representative health check.', 'Validate failure and recovery behavior.'],
    practices: ['Do not use a shallow health check that ignores dependencies.', 'Keep backend ingress limited to the load-balancer path.', 'Use TLS deliberately.', 'Test member removal before production.'],
    level: 'Intermediate',
  }),
  serviceArticle({
    slug: 'service-dns',
    title: 'DNS',
    summary: 'Create zones and records, delegate nameservers, and manage application naming in Nexus.',
    category: 'Networking',
    service: 'dns',
    sourcePaths: ['frontend/src/pages/nexus/services/dns'],
    concepts: ['A zone is authoritative only after correct delegation.', 'Records map names to addresses or other DNS data.', 'TTL controls cache duration.', 'Record management can remain locked until delegation validates.'],
    workflow: ['Create the DNS zone.', 'Copy the assigned Nexus nameservers.', 'Update delegation at the domain registrar or parent zone.', 'Wait for validation.', 'Create records and verify authoritative answers.'],
    practices: ['Lower TTL before planned migrations.', 'Avoid conflicting records.', 'Document external dependencies.', 'Use staged validation before changing production names.'],
  }),
  serviceArticle({
    slug: 'service-ip-transit',
    title: 'IP Transit',
    summary: 'Manage ASNs, routed prefixes, BGP sessions, and supporting documents for advanced network connectivity.',
    category: 'Networking',
    service: 'ip-transit',
    sourcePaths: ['frontend/src/pages/nexus/services/ip-transit'],
    concepts: ['ASN identifies an autonomous routing domain.', 'Prefixes are the routes exchanged or managed.', 'BGP sessions exchange reachability.', 'Documents support authorization and provisioning requirements.'],
    workflow: ['Review service prerequisites and documentation.', 'Register or select the ASN.', 'Add authorized prefixes.', 'Create a BGP session with agreed parameters.', 'Validate session state and received or advertised routes.'],
    practices: ['Use strict route filters.', 'Set maximum-prefix controls.', 'Coordinate maintenance windows.', 'Monitor route and session changes.'],
    level: 'Advanced',
    minutes: 18,
  }),
  serviceArticle({
    slug: 'service-volumes',
    title: 'Block Storage',
    summary: 'Create persistent volumes, attach them to compute, extend capacity, and manage volume lifecycle.',
    category: 'Storage',
    service: 'volumes',
    sourcePaths: ['frontend/src/pages/nexus/services/volumes'],
    concepts: ['A volume is persistent block storage independent of a server lifecycle.', 'Volume type represents a storage performance class.', 'Attachment connects a volume to compute.', 'Extending provider capacity also requires guest filesystem expansion.'],
    workflow: ['Choose location, type, and capacity.', 'Create the volume.', 'Attach it to a compatible server.', 'Format and mount it in the guest.', 'Monitor capacity and extend safely when required.'],
    practices: ['Use stable filesystem identifiers.', 'Back up important data.', 'Unmount before risky detach operations.', 'Delete unused detached volumes explicitly.'],
  }),
  serviceArticle({
    slug: 'service-s3',
    title: 'S3-Compatible Object Storage',
    summary: 'Create buckets and credentials for durable object-based application data and artifacts.',
    category: 'Storage',
    service: 's3',
    sourcePaths: ['frontend/src/pages/nexus/services/s3'],
    concepts: ['Buckets contain objects addressed by keys.', 'Credentials grant programmatic API access.', 'Object storage is not a mounted block filesystem.', 'Application access should use scoped, rotated credentials.'],
    workflow: ['Create an S3 credential.', 'Store the secret key securely when shown.', 'Create a bucket with a deliberate naming and lifecycle scheme.', 'Configure an S3-compatible client.', 'Upload, retrieve, and delete a test object.'],
    practices: ['Never expose secret keys in browser applications.', 'Separate credentials by workload.', 'Plan retention and restore.', 'Use versioned object names for releases.'],
  }),
  serviceArticle({
    slug: 'service-database',
    title: 'Managed Databases',
    summary: 'Provision database instances, manage database users, connect applications, and monitor data-service usage.',
    category: 'Data',
    service: 'database',
    sourcePaths: ['frontend/src/pages/nexus/services/database'],
    concepts: ['Database instance provides the managed engine runtime.', 'Database users should be separated by application responsibility.', 'Connectivity and credentials are independent controls.', 'Capacity and backup requirements must match business recovery goals.'],
    workflow: ['Choose engine, size, location, and network settings.', 'Create the database and wait for readiness.', 'Create a least-privileged database user.', 'Store credentials in Secrets.', 'Connect from an authorized application path and test recovery procedures.'],
    practices: ['Do not use an administrative account in application code.', 'Rotate credentials.', 'Monitor storage and connection pressure.', 'Test backups and migrations before production changes.'],
    level: 'Intermediate',
  }),
  serviceArticle({
    slug: 'service-pki',
    title: 'PKI and Certificates',
    summary: 'Create certificate authorities, issue certificates, define roles, and automate certificate workflows with ACME.',
    category: 'Security',
    service: 'pki',
    sourcePaths: ['frontend/src/pages/nexus/services/pki', 'docs/vault/nexus-pki-token-policy.hcl'],
    concepts: ['Certificate authority establishes a trust root or intermediate.', 'Roles constrain allowed certificate identities and lifetimes.', 'Issuance creates a certificate and private key.', 'ACME automates compatible certificate enrollment and renewal.'],
    workflow: ['Define the trust model and certificate consumers.', 'Create or configure a CA.', 'Create a restrictive issuance role.', 'Issue a short-lived test certificate.', 'Configure renewal and revocation ownership.'],
    practices: ['Protect CA material.', 'Prefer short lifetimes with automation.', 'Constrain names and usages.', 'Monitor expiration and revocation processes.'],
    level: 'Advanced',
  }),
  serviceArticle({
    slug: 'service-secrets',
    title: 'Secrets',
    summary: 'Store sensitive application values separately from source code and deployment configuration.',
    category: 'Security',
    service: 'secrets',
    sourcePaths: ['frontend/src/pages/nexus/services/secrets'],
    concepts: ['Secret metadata identifies purpose without revealing value.', 'Consumers should receive only required secrets.', 'Rotation changes both stored value and dependent application configuration.', 'Deletion can immediately break consumers.'],
    workflow: ['Create a secret with a clear name and owner.', 'Enter the value through the protected workflow.', 'Grant or configure the intended consumer.', 'Validate application access without logging the value.', 'Rotate and retire according to policy.'],
    practices: ['Never paste secrets into tickets or logs.', 'Use separate secrets per environment.', 'Record rotation ownership.', 'Revoke exposed values immediately.'],
  }),
  serviceArticle({
    slug: 'service-tenants',
    title: 'Tenant Management',
    summary: 'Organize Nexus ownership, membership, service access, billing, and quotas around durable tenant boundaries.',
    category: 'Platform',
    service: 'tenants',
    sourcePaths: ['frontend/src/pages/nexus/NexusTenants.tsx', 'frontend/src/pages/nexus/NexusMembers.tsx'],
    concepts: ['Tenant is the resource and billing boundary.', 'Members receive tenant-scoped access.', 'Selected tenant determines the active control-plane context.', 'Eligibility and service availability can vary by tenant.'],
    workflow: ['Create or select the tenant.', 'Complete required account and billing information.', 'Add members and assign appropriate access.', 'Review enabled services and quotas.', 'Audit membership and ownership regularly.'],
    practices: ['Avoid shared accounts.', 'Keep ownership current.', 'Separate tenants only for real governance boundaries.', 'Document tenant purpose and cost center.'],
  }),
  serviceArticle({
    slug: 'service-catalog-active-services',
    title: 'Service Catalog and Active Services',
    summary: 'Discover available Nexus capabilities and maintain a cross-service inventory of resources currently active in a tenant.',
    category: 'Platform',
    service: 'catalog',
    sourcePaths: ['frontend/src/pages/nexus/NexusCatalog.tsx', 'frontend/src/pages/nexus/NexusActiveServices.tsx'],
    concepts: ['Service Catalog describes enabled capabilities and billing modes.', 'Active Services summarizes provisioned tenant resources.', 'Availability can vary by tenant eligibility and platform configuration.', 'Inventory and usage should be reviewed together.'],
    workflow: ['Select the intended tenant.', 'Open Service Catalog and review enabled services.', 'Open Active Services and inspect current resource counts.', 'Drill into unexpected resources.', 'Compare inventory with Usage and resolve stale ownership.'],
    practices: ['Review inventory weekly.', 'Name resources by owner and purpose.', 'Remove abandoned dependencies.', 'Use service guides before enabling unfamiliar systems.'],
  }),
  serviceArticle({
    slug: 'service-billing',
    title: 'Billing, Usage, Invoices, and Credits',
    summary: 'Understand tenant billing state, metered usage, invoices, payment methods, credits, and manual or automated settlement.',
    category: 'Platform',
    service: 'billing',
    sourcePaths: [
      'frontend/src/pages/nexus/NexusBillingOverview.tsx',
      'frontend/src/pages/nexus/NexusUsageDashboard.tsx',
      'frontend/src/pages/nexus/NexusInvoices.tsx',
      'frontend/src/pages/nexus/NexusPaymentMethods.tsx',
      'frontend/src/pages/nexus/NexusCredits.tsx',
    ],
    concepts: ['Usage records measure billable resource consumption.', 'Invoices finalize charges for a billing period.', 'Credits reduce eligible balances.', 'Payment methods and settlement mode determine collection workflow.', 'Billing eligibility can affect paid-service actions.'],
    workflow: ['Complete billing information.', 'Add an eligible payment method when required.', 'Review current usage by service.', 'Inspect invoices and transaction status.', 'Reconcile credits and contact support for unexplained discrepancies.'],
    practices: ['Review usage before invoice finalization.', 'Keep billing contacts current.', 'Do not assume deleting one resource removes its dependencies.', 'Use quotas and budgets together.'],
  }),
  serviceArticle({
    slug: 'service-support',
    title: 'Nexus Support',
    summary: 'Use support plans and tickets to coordinate technical, billing, and account assistance.',
    category: 'Platform',
    service: 'support',
    sourcePaths: ['frontend/src/pages/nexus/NexusSupportPage.tsx', 'frontend/src/pages/nexus/NexusSupportTicketPage.tsx'],
    concepts: ['Support plan defines available coverage.', 'Ticket is the durable communication record.', 'Department routing sends the issue to the appropriate team.', 'Sensitive credentials must never be included.'],
    workflow: ['Review service status and local troubleshooting evidence.', 'Create a ticket with tenant and resource context.', 'Choose the correct department.', 'Respond in the same ticket as investigation progresses.', 'Confirm resolution and record internal follow-up actions.'],
    practices: ['Use exact timestamps and resource IDs.', 'Describe impact and reproduction.', 'Sanitize attachments.', 'Keep one issue per ticket when practical.'],
  }),
  {
    slug: 'active-services-inventory',
    title: 'Inventory Active Services',
    summary: 'Use the Active Services center to understand everything currently provisioned in a tenant.',
    track: 'operations',
    category: 'Inventory',
    level: 'Beginner',
    minutes: 10,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/NexusActiveServices.tsx'],
    sections: [
      section('purpose', 'Why inventory matters', [
        'Active Services provides a cross-service view of current tenant resources. Use it to find abandoned resources, understand architecture dependencies, and reconcile expected usage.',
      ]),
      {
        id: 'review',
        title: 'Weekly review',
        steps: [
          'Open Active Services for the intended tenant.',
          'Review each service count and drill into unexpected resources.',
          'Identify unattached, idle, failed, or ownerless resources.',
          'Compare the inventory with Usage.',
          'Delete or release resources only after confirming dependencies and retention requirements.',
        ],
      },
    ],
  },
  {
    slug: 'usage-cost-operations',
    title: 'Monitor Usage and Cost',
    summary: 'Build a repeatable process for usage review, forecasting, anomaly detection, and cleanup.',
    track: 'operations',
    category: 'FinOps',
    level: 'Intermediate',
    minutes: 18,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/NexusUsageDashboard.tsx', 'frontend/src/pages/nexus/NexusBillingOverview.tsx'],
    sections: [
      section('cadence', 'Operating cadence', [
        'Review usage often enough that an unexpected resource cannot run for an entire billing cycle unnoticed. Weekly review is a practical default for active environments.',
      ]),
      {
        id: 'signals',
        title: 'Signals to investigate',
        bullets: [
          'A new service category appears without a planned change.',
          'Public IP count exceeds the number of intended public endpoints.',
          'Storage grows without corresponding application data growth.',
          'Compute remains consistently underused or saturated.',
          'Usage continues after a workload was believed to be deleted.',
        ],
      },
      {
        id: 'response',
        title: 'Response workflow',
        steps: ['Confirm the tenant and billing period.', 'Identify the exact resource.', 'Find the owner and business purpose.', 'Resize, release, or retain based on evidence.', 'Record the decision and expected future usage.'],
      },
    ],
  },
  {
    slug: 'quotas-and-guardrails',
    title: 'Quotas and Guardrails',
    summary: 'Use tenant quotas to limit accidental growth while preserving enough headroom for normal operations.',
    track: 'operations',
    category: 'Governance',
    level: 'Intermediate',
    minutes: 14,
    reviewed,
    sourcePaths: ['internal/services/nexus_quota_service.go', 'docs/quota-acceptance-checklist.md'],
    sections: [
      section('purpose', 'What quotas do', [
        'Quotas cap resource usage or actions for a tenant. They reduce accidental growth and communicate platform limits, but they do not replace budgets, monitoring, or architecture review.',
      ]),
      {
        id: 'set',
        title: 'Set useful limits',
        bullets: [
          'Base limits on expected steady state plus documented growth headroom.',
          'Keep scarce resources such as public addresses intentionally constrained.',
          'Review quota failures before raising a limit.',
          'Use temporary increases for controlled migrations when appropriate.',
        ],
      },
      section('request', 'Quota increase requests', [
        'Provide the current use, requested limit, business purpose, expected duration, architecture impact, and cost owner. A strong request explains why optimization or cleanup is not sufficient.',
      ]),
    ],
  },
  {
    slug: 'troubleshoot-resource-actions',
    title: 'Troubleshoot Resource Actions',
    summary: 'Diagnose failed, delayed, or blocked Nexus actions without creating duplicate resources or increasing impact.',
    track: 'operations',
    category: 'Troubleshooting',
    level: 'Intermediate',
    minutes: 20,
    reviewed,
    sourcePaths: ['frontend/src/lib/nexusServiceAvailability.ts', 'internal/services/nexus_action_lock_cleanup_worker.go'],
    sections: [
      {
        id: 'sequence',
        title: 'Troubleshooting sequence',
        steps: [
          'Confirm the selected tenant and location.',
          'Read the full error and resource task state.',
          'Check billing eligibility, quota, and service availability.',
          'Verify dependent resources still exist and are compatible.',
          'Wait for an in-progress asynchronous action before retrying.',
          'Refresh the resource detail and Active Services views.',
          'Open a support ticket when state remains inconsistent.',
        ],
      },
      {
        id: 'avoid',
        title: 'Avoid',
        bullets: [
          'Repeatedly clicking Create or Delete.',
          'Deleting dependencies while an action is in progress.',
          'Changing several variables before testing again.',
          'Sharing credentials or secret values in troubleshooting evidence.',
        ],
      },
    ],
  },
  {
    slug: 'data-corrections',
    title: 'Understand Data Corrections',
    summary: 'Recognize provider-to-Nexus discrepancies and understand why billing or resource state may be suspended pending correction.',
    track: 'operations',
    category: 'Reconciliation',
    level: 'Advanced',
    minutes: 16,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/NexusDataCorrections.tsx', 'internal/services/nexus_data_corrections_service.go'],
    sections: [
      section('discrepancy', 'What a discrepancy means', [
        'Nexus maintains local ownership and billing records while provider systems maintain the underlying cloud resource. A discrepancy exists when those records no longer correspond.',
        'Examples include a provider resource with no Nexus record or a Nexus record whose provider resource is missing. Automated checks surface these conditions for controlled correction.',
      ]),
      {
        id: 'response',
        title: 'Response principles',
        bullets: [
          'Do not create duplicate records without verifying provider identity.',
          'Match tenant, location, resource type, provider ID, size, and creation context.',
          'Preserve auditability when adopting or resolving a resource.',
          'Verify billing behavior after correction.',
        ],
        note: {
          tone: 'warning',
          title: 'Administrator workflow',
          body: 'Data correction changes ownership or billing records and should be performed only through approved administrative procedures.',
        },
      },
    ],
  },
  {
    slug: 'resource-retirement',
    title: 'Retire Nexus Resources Safely',
    summary: 'Delete workloads without leaving billable dependencies or destroying data that must be retained.',
    track: 'operations',
    category: 'Lifecycle',
    level: 'Intermediate',
    minutes: 15,
    reviewed,
    sourcePaths: ['frontend/src/pages/nexus/NexusActiveServices.tsx'],
    sections: [
      {
        id: 'checklist',
        title: 'Retirement checklist',
        steps: [
          'Confirm the owner approved retirement.',
          'Identify data retention, legal, backup, and restore requirements.',
          'Remove the resource from DNS, load balancers, deployment automation, and monitoring.',
          'Back up or migrate persistent data.',
          'Delete the primary workload.',
          'Delete or release independent volumes, snapshots, images, public IPs, credentials, and secrets.',
          'Verify Active Services and Usage no longer show unintended resources.',
          'Update architecture and inventory records.',
        ],
      },
      section('order', 'Delete in dependency order', [
        'Remove traffic and consumers before providers. For a web application, remove DNS or traffic, detach load-balancer members, stop application writes, preserve data, delete compute, then release independent network and storage resources.',
      ]),
    ],
  },
];

export const articleBySlug = new Map(articles.map((article) => [article.slug, article]));

export const articlesByTrack = tracks.reduce<Record<TrackId, DocArticle[]>>(
  (result, track) => {
    result[track.id] = articles.filter((article) => article.track === track.id);
    return result;
  },
  {
    'get-started': [],
    training: [],
    architecture: [],
    services: [],
    operations: [],
  },
);

export const serviceCategories = ['Compute', 'Networking', 'Storage', 'Data', 'Security', 'Platform'];

export const searchableText = (article: DocArticle) =>
  [
    article.title,
    article.summary,
    article.category,
    article.service,
    ...article.sections.flatMap((item) => [
      item.title,
      ...(item.paragraphs ?? []),
      ...(item.bullets ?? []),
      ...(item.steps ?? []),
      item.note?.title,
      item.note?.body,
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
